import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Mail, Save, ShieldCheck, Bell, BellOff } from "lucide-react";

/**
 * Iter122bf · Preferenze email dello Studio.
 * - Email dedicata alle notifiche (se compilata → riceve tutte, invece dell'email principale)
 * - Toggle per categoria (manutenzioni, preventivi, sinistri, ...)
 *
 * Endpoint: GET/PUT /api/settings/email-prefs
 * Visibile a: role='admin' (require_role backend). SuperAdmin lo può vedere in ottica Studio Izzo.
 */
export default function EmailPreferencesCard() {
  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState(false);
  const [emailInput, setEmailInput] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/settings/email-prefs");
      setPrefs(data);
      setEmailInput(data.email_notifiche_indirizzo || "");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore caricamento preferenze"); }
  };
  useEffect(() => { load(); }, []);

  const toggleCat = (k) => setPrefs((p) => ({ ...p, categorie: { ...p.categorie, [k]: !p.categorie[k] } }));
  const setTutte = (val) => setPrefs((p) => {
    const cats = { ...p.categorie };
    Object.keys(cats).forEach((k) => { cats[k] = val; });
    return { ...p, categorie: cats };
  });

  const salva = async () => {
    setBusy(true);
    try {
      await api.put("/settings/email-prefs", {
        email_notifiche_indirizzo: emailInput.trim(),
        categorie: prefs.categorie,
      });
      toast.success("Preferenze salvate");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Salvataggio non riuscito");
    }
    setBusy(false);
  };

  if (!prefs) return <Card className="p-5 border-2 animate-pulse text-slate-400" data-testid="email-prefs-loading">Caricamento preferenze email…</Card>;

  const attive = Object.values(prefs.categorie).filter(Boolean).length;
  const totali = (prefs.catalogo || []).length;

  return (
    <Card className="p-5 border-2" data-testid="email-prefs-card">
      <div className="flex items-center gap-2 mb-3">
        <Mail className="w-5 h-5 text-brand" />
        <h3 className="font-display text-lg font-bold text-slate-800">Preferenze notifiche via email</h3>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Riduci il rumore nella casella email dello Studio scegliendo un indirizzo dedicato e selezionando solo le categorie di notifica che ti interessano.
      </p>

      {/* Sezione: indirizzo dedicato */}
      <div className="mb-6 p-4 rounded-lg border-2 border-slate-100 bg-slate-50/50" data-testid="email-prefs-indirizzo">
        <label className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-brand" /> Email dedicata alle notifiche <span className="text-slate-400 font-normal">(consigliato)</span></label>
        <p className="text-xs text-slate-500 mt-1 mb-2">
          Crea presso il tuo provider (Gmail, Aruba, Outlook…) un indirizzo separato solo per Ginius,
          poi indicalo qui. Se lasci vuoto, le notifiche arrivano all'email principale dello Studio.
        </p>
        <div className="flex flex-wrap gap-2">
          <Input
            type="email"
            data-testid="email-prefs-indirizzo-input"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            placeholder="es. notifiche.ginius@iltuostudio.it"
            className="h-11 flex-1 min-w-[220px] max-w-md"
          />
          {emailInput && (
            <Button variant="outline" onClick={() => setEmailInput("")} data-testid="email-prefs-indirizzo-clear" className="h-11">
              Rimuovi
            </Button>
          )}
        </div>
      </div>

      {/* Sezione: categorie on/off */}
      <div className="mb-4" data-testid="email-prefs-categorie">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <label className="text-sm font-semibold text-slate-700">Categorie di notifica email ({attive}/{totali} attive)</label>
          <div className="flex gap-2">
            <button data-testid="email-prefs-all-on" onClick={() => setTutte(true)} className="text-xs font-semibold underline text-brand">Attiva tutte</button>
            <button data-testid="email-prefs-all-off" onClick={() => setTutte(false)} className="text-xs font-semibold underline text-slate-500">Disattiva tutte</button>
          </div>
        </div>
        <div className="space-y-2">
          {(prefs.catalogo || []).map((c) => {
            const on = !!prefs.categorie[c.key];
            return (
              <label key={c.key} data-testid={`email-prefs-toggle-${c.key}`}
                     className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${on ? "border-brand/30 bg-brand/5" : "border-slate-200 bg-white opacity-70"}`}>
                <input type="checkbox" checked={on} onChange={() => toggleCat(c.key)} className="mt-1 w-4 h-4 accent-brand" />
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-800 flex items-center gap-1.5">
                    {on ? <Bell className="w-4 h-4 text-brand" /> : <BellOff className="w-4 h-4 text-slate-400" />}
                    {c.label}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{c.desc}</div>
                </div>
              </label>
            );
          })}
        </div>
      </div>

      <p className="text-xs text-slate-400 mb-3">
        Le notifiche di <b>sistema</b> (benvenuto, reset password, attivazione account, avvisi di sicurezza) restano sempre attive.
      </p>

      <Button data-testid="email-prefs-salva" disabled={busy} onClick={salva} className="h-11 bg-brand hover:bg-brand-dark font-bold">
        <Save className="w-4 h-4 mr-2" /> {busy ? "Salvataggio…" : "Salva preferenze"}
      </Button>
    </Card>
  );
}
